import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Menu, MenuItem, CircularProgress, ListItemIcon, ListItemText, Chip, Tooltip, Backdrop, Divider, TableSortLabel, Avatar, Drawer, Select } from '@mui/material';
import { useNotification } from '../layouts/Layout';
import { MoreVert, Send, Delete, MailOutline as GmailIcon, Visibility, UnfoldMore, Refresh, ContentCopy, CalendarMonth } from '@mui/icons-material';
import { Close, DragIndicator } from '@mui/icons-material';
import EditIcon from '../icons/EditIcon';
import AddInquiryForm from './AddInquiryForm';
import OfferInquiryEmailDialog from './OfferInquiryEmailDialog';
import SubcontractorDetailsDrawer from './SubcontractorDetailsDrawer';
import InquiryNotesDrawer from './InquiryNotesDrawer';
import { offerStandaloneManageService, type OfferStandaloneInquiryDTO, type OfferStandalonePackageDTO } from '../../services/offerStandaloneManageService';
import { useAuth } from '../../contexts/AuthContext';

interface InquiryUI {
  id: string;
  packageId: string;
  packageName: string;
  contractorEmail: string;
  contractorPhone1?: string;
  contractorPhone2?: string;
  subject: string;
  status: 'Przygotowane' | 'draft gmail' | 'Wysłane' | 'Odpowiedź' | 'Odmówiono';
  createdAt: string;
  notes?: string;
  lastContactDate?: string;
  plannedContactDate?: string;
  gmailDraftId?: string;
  gmailMessageId?: string;
  createdByName?: string;
  createdByAvatarUrl?: string;
  inviteToCollectifileBoard?: boolean;
}

interface InquiriesTabProps {
  projectName: string;
  offerId: string;
  offerDisplayId: string;
  hasCollectifileBoard?: boolean;
  onRefreshData?: () => void;
  onOpenAddReceivedOffer?: (inquiry: { inquiryId: string; contractorEmail: string; packageId?: string }) => void;
}

const InquiriesTab: React.FC<InquiriesTabProps> = ({ projectName, offerId, offerDisplayId, hasCollectifileBoard = false, onRefreshData, onOpenAddReceivedOffer }) => {
  const [inquiries, setInquiries] = useState<OfferStandaloneInquiryDTO[]>([]);
  const [packages, setPackages] = useState<OfferStandalonePackageDTO[]>([]);
  const [receivedOffers, setReceivedOffers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showNotification } = useNotification();
  const { user } = useAuth();

  const [inquiriesUI, setInquiriesUI] = useState<InquiryUI[]>([]);
  const [openInquiryForm, setOpenInquiryForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<InquiryUI | null>(null);
  const [initialPackage, setInitialPackage] = useState<OfferStandalonePackageDTO | null>(null);
  const [inquiryAnchorEl, setInquiryAnchorEl] = useState<null | HTMLElement>(null);
  const [actionInquiryIndex, setActionInquiryIndex] = useState<number>(-1);
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [openBulkActionsDrawer, setOpenBulkActionsDrawer] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [previousStatuses, setPreviousStatuses] = useState<Record<string, string>>({});
  const [openSubcontractorDrawer, setOpenSubcontractorDrawer] = useState(false);
  const [selectedContractorEmail, setSelectedContractorEmail] = useState<string>('');
  const [selectedInquiryDTO, setSelectedInquiryDTO] = useState<OfferStandaloneInquiryDTO | null>(null);
  const [selectedPackageName, setSelectedPackageName] = useState<string>('');
  const [openNotesDrawer, setOpenNotesDrawer] = useState(false);
  const [selectedInquiryForNotes, setSelectedInquiryForNotes] = useState<OfferStandaloneInquiryDTO | null>(null);
  const dateInputRef = React.useRef<HTMLInputElement | null>(null);
  const pendingDateUpdate = React.useRef<{ inquiryId: string; field: 'last_contact_date' | 'planned_contact_date' } | null>(null);

  const handleUpdateInquiryField = React.useCallback(async (inquiryId: string, field: 'last_contact_date' | 'planned_contact_date', value: string | null | undefined) => {
    try {
      await offerStandaloneManageService.updateInquiry(offerId, inquiryId, { [field]: value === '' ? null : value });
      loadData({ silent: true });
      onRefreshData && onRefreshData();
      showNotification('Zapisano.', 'success');
    } catch {
      showNotification('Nie udało się zapisać.', 'error');
    }
  }, [offerId, onRefreshData, showNotification]);

  // Tryb zmiany szerokości kolumn
  const [colResizeMode, setColResizeMode] = React.useState<boolean>(false);
  const [dragCol, setDragCol] = React.useState<{ key: string; startX: number; startW: number; rightKey?: string; startWRight?: number } | null>(null);
  const defaultColumnWidths = React.useMemo<Record<string, number>>(() => ({
    id: 150,
    kp: 56,
    package: 200,
    contractor: 250,
    subject: 280,
    status: 120,
    created: 120,
    last_contact: 120,
    planned_contact: 120,
    notes: 180,
    actions: 80,
  }), []);
  const STORAGE_KEY = React.useMemo(() => 'offerInquiriesColWidths', []);
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>(defaultColumnWidths);
  const getWidthNum = React.useCallback((key: string) => columnWidths[key] ?? defaultColumnWidths[key] ?? 40, [columnWidths, defaultColumnWidths]);
  const getColW = React.useCallback((key: string) => `${getWidthNum(key)}px`, [getWidthNum]);

  // Konfiguracja widoczności i kolejności kolumn
  const ALL_COLUMNS = React.useMemo<string[]>(() => ['id','kp','package','contractor','subject','status','created','last_contact','planned_contact','notes','actions'], []);
  const STORAGE_PREFIX = 'offerInquiriesTableCol';
  const VISIBLE_KEY = `${STORAGE_PREFIX}:visible`;
  const ORDER_KEY = `${STORAGE_PREFIX}:order`;
  const [columnOrder, setColumnOrder] = React.useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const ord = JSON.parse(raw);
        if (Array.isArray(ord) && ord.length) {
          const filtered = ord.filter((k:string)=>ALL_COLUMNS.includes(k));
          // Zawsze upewnij się, że 'kp' jest zaraz po 'id'
          if (filtered.includes('id')) {
            const idIndex = filtered.indexOf('id');
            const kpIndex = filtered.indexOf('kp');
            // Jeśli 'kp' istnieje, usuń je z obecnej pozycji
            if (kpIndex >= 0 && kpIndex !== idIndex + 1) {
              filtered.splice(kpIndex, 1);
            }
            // Jeśli 'kp' nie istnieje lub zostało usunięte, dodaj je zaraz po 'id'
            if (!filtered.includes('kp')) {
              filtered.splice(idIndex + 1, 0, 'kp');
            }
          }
          // Dodaj pozostałe brakujące kolumny na końcu
          const missing = ALL_COLUMNS.filter(k => !filtered.includes(k));
          return filtered.concat(missing);
        }
      }
    } catch {}
    return [...ALL_COLUMNS];
  });
  
  // Upewnij się, że 'kp' jest zawsze zaraz po 'id' (korekta w czasie rzeczywistym)
  React.useEffect(() => {
    if (columnOrder.includes('id') && columnOrder.includes('kp')) {
      const idIndex = columnOrder.indexOf('id');
      const kpIndex = columnOrder.indexOf('kp');
      if (kpIndex !== idIndex + 1) {
        const corrected = [...columnOrder];
        corrected.splice(kpIndex, 1);
        corrected.splice(idIndex + 1, 0, 'kp');
        setColumnOrder(corrected);
        try {
          localStorage.setItem(ORDER_KEY, JSON.stringify(corrected));
        } catch {}
      }
    }
  }, [columnOrder]);
  const [columnsVisible, setColumnsVisible] = React.useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(VISIBLE_KEY);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object') return obj;
      }
    } catch {}
    return {};
  });
  const colV = React.useCallback((key: string) => columnsVisible[key] !== false, [columnsVisible]);

  // Panel „Dostosuj kolumny”
  const [colsOpen, setColsOpen] = React.useState<boolean>(false);
  const [draftColumnsVisible, setDraftColumnsVisible] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrder, setDraftOrder] = React.useState<string[] | null>(null);
  const [draftWidths, setDraftWidths] = React.useState<Record<string, number>>({});
  const [colsPosition, setColsPosition] = React.useState<{ top:number; left:number }>({ top: 0, left: 0 });
  const [colsDragging, setColsDragging] = React.useState<{ startX:number; startY:number; top:number; left:number } | null>(null);
  const COLUMNS_DIALOG = { width: 305, height: 420 } as const;

  React.useEffect(() => {
    if (!colsDragging) return;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - colsDragging.startX;
      const dy = e.clientY - colsDragging.startY;
      const nextTop = Math.max(0, Math.min((window?.innerHeight || 0) - COLUMNS_DIALOG.height, colsDragging.top + dy));
      const nextLeft = Math.max(0, Math.min((window?.innerWidth || 0), colsDragging.left + dx));
      setColsPosition({ top: nextTop, left: nextLeft });
    };
    const onUp = () => setColsDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [colsDragging]);

  // Inicjalizacja szerokości z localStorage (tylko raz przy starcie)
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setColumnWidths((prev) => {
            // Połącz zapisane wartości z domyślnymi, aby upewnić się że wszystkie kolumny mają szerokość
            return { ...defaultColumnWidths, ...parsed };
          });
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Tylko raz przy montowaniu

  // Zapis ustawień do localStorage
  React.useEffect(() => { 
    try { localStorage.setItem(ORDER_KEY, JSON.stringify(columnOrder)); } catch {} 
  }, [ORDER_KEY, columnOrder]);
  
  React.useEffect(() => { 
    try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(columnsVisible)); } catch {} 
  }, [VISIBLE_KEY, columnsVisible]);
  
  React.useEffect(() => { 
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(columnWidths)); } catch {} 
  }, [STORAGE_KEY, columnWidths]);

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const minW = 24;
      const newW = Math.max(minW, dragCol.startW + dx);
      if (dragCol.rightKey && typeof dragCol.startWRight === 'number') {
        const newRight = Math.max(minW, (dragCol.startWRight as number) - dx);
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW, [dragCol.rightKey as string]: newRight }));
        setDraftWidths((prev) => ({ ...prev, [dragCol.key]: newW, [dragCol.rightKey as string]: newRight }));
      } else {
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
        setDraftWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
      }
    };
    const onUp = () => setDragCol(null);
    if (dragCol) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { once: true } as any);
    }
    return () => { window.removeEventListener('mousemove', onMove); };
  }, [dragCol]);

  // Obsługa ESC do zamykania okienka "Dostosuj kolumny"
  React.useEffect(() => {
    if (!colsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        setColsOpen(false);
        setColResizeMode(false);
        setDraftColumnsVisible(null);
        setDraftOrder(null);
        setDraftWidths({});
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => { window.removeEventListener('keydown', onKeyDown); };
  }, [colsOpen]);

  const renderResizableTh = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center', sortable: boolean = false, tooltipTitle?: string) => {
    const sortKey = keyId as SortKey;
    const isSortable = sortable && (sortKey === 'id' || sortKey === 'kp' || sortKey === 'package' || sortKey === 'contractor' || sortKey === 'subject' || sortKey === 'status' || sortKey === 'created');
    const content = keyId === 'id' ? <Box sx={{ pl: '16px' }}>{node}</Box> : node;
    const wrappedContent = tooltipTitle ? (
      <Tooltip title={tooltipTitle} arrow>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start', width: '100%' }}>
          {content}
        </span>
      </Tooltip>
    ) : content;
    
    return (
    <TableCell align={align} sx={{ padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, width: getColW(keyId), maxWidth: 'none', position: 'relative', userSelect: 'none', backgroundColor: '#FAFAFA', borderRight: keyId === 'actions' ? 'none' : '1px solid #d4d4d9' }}>
      {isSortable ? (
        <TableSortLabel {...getSortLabelProps(sortKey)}>
          {wrappedContent}
        </TableSortLabel>
      ) : wrappedContent}
      {colResizeMode ? (
        <Box
          onMouseDown={(e) => {
            e.preventDefault(); e.stopPropagation();
            const visOrder = (draftOrder ?? columnOrder).filter((k)=> colV(k));
            const idx = visOrder.indexOf(keyId);
            const rightKey = idx >= 0 && idx < visOrder.length - 1 ? visOrder[idx+1] : undefined;
            setDragCol({
              key: keyId,
              startX: e.clientX,
              startW: getWidthNum(keyId),
              rightKey,
              startWRight: rightKey ? getWidthNum(rightKey) : undefined
            });
          }}
          sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none',
            backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }}
        />
      ) : null}
    </TableCell>
    );
  };

  const statusToUI = (status: string): 'Przygotowane' | 'draft gmail' | 'Wysłane' | 'Odpowiedź' | 'Odmówiono' => {
    switch ((status || '').toLowerCase()) {
      case 'przygotowane': return 'Przygotowane';
      case 'draft gmail': return 'draft gmail';
      case 'wysłane': return 'Wysłane';
      case 'odpowiedź': return 'Odpowiedź';
      case 'odmówiono': return 'Odmówiono';
      default: return 'Przygotowane';
    }
  };

  const renderStatusChip = (status: string, inquiryId: string) => {
    // Sprawdź czy zapytanie ma powiązaną ofertę otrzymaną (received offer)
    const hasLinkedOffer = receivedOffers.some(ro => ro.inquiry_id === inquiryId);
    
    // Sprawdź oryginalne zapytanie
    const originalInquiry = inquiries.find(i => i.inquiry_id === inquiryId);
    const isSent = originalInquiry?.status === 'Wysłane';
    
    // Określ status do wyświetlenia (priorytet: Oferta > Odpowiedź > Wysłane > oryginalny status)
    // Logika zgodna z wymaganiami użytkownika:
    // 1. Jeśli zapytanie ma received offer → "Oferta" (użytkownik dodał ofertę i powiązał ją z zapytaniem)
    // 2. Jeśli zapytanie ma status "Odpowiedź" (ustawiony przez backend gdy wykryje odpowiedź) → "Odpowiedź"
    // 3. Jeśli zapytanie jest wysłane → "Wysłane"
    let displayStatus = status;
    if (hasLinkedOffer) {
      displayStatus = 'Oferta'; // Ma powiązaną ofertę otrzymaną
    } else if (status === 'Odpowiedź' || originalInquiry?.status === 'Odpowiedź') {
      displayStatus = 'Odpowiedź'; // Backend automatycznie ustawił status "Odpowiedź" gdy wykrył odpowiedź
    } else if (isSent) {
      displayStatus = 'Wysłane'; // Wysłane
    }
    
    const map: Record<string, { bg: string; label: string }> = {
      'Przygotowane': { bg: '#E3F2FD', label: 'Przygotowane' }, // Niebieski
      'draft gmail': { bg: '#FFF8E1', label: 'Draft Gmail' }, // Żółty
      'Wysłane': { bg: '#E8F5E9', label: 'Wysłane' }, // Zielony
      'Odpowiedź': { bg: '#FFCDD2', label: 'Odpowiedź' }, // Czerwony (jaśniejszy)
      'Oferta': { bg: '#E1BEE7', label: 'Oferta' }, // Pastelowy fiolet
      'Odmówiono': { bg: '#FFE5CC', label: 'Odmówiono' }, // Pomarańczowy pastelowy
    };
    const s = map[displayStatus] || { bg: '#f5f5f5', label: displayStatus };
    const textColor = '#020242';
    const isBlinking = displayStatus === 'Odpowiedź';
    const replyMessageId = originalInquiry?.gmail_reply_message_id;
    const threadId = originalInquiry?.gmail_thread_id;
    
    // Sprawdź, czy zalogowany użytkownik to ten sam, który wysłał zapytanie
    // Link do Gmail powinien być widoczny tylko dla użytkownika, który wysłał zapytanie
    const isInquirySender = user && originalInquiry?.created_by_user_id && 
                            user.user_id === originalInquiry.created_by_user_id;
    
    // Utwórz link do Gmail dla statusu "Odpowiedź" - tylko jeśli zalogowany użytkownik wysłał zapytanie
    // Jeśli mamy messageId odpowiedzi, użyj go do bezpośredniego linku do odpowiedzi (#all/{messageId})
    // W przeciwnym razie użyj threadId (otworzy cały wątek)
    const gmailLink = isBlinking && isInquirySender && (replyMessageId || threadId)
      ? replyMessageId 
        ? `https://mail.google.com/mail/u/0/#all/${replyMessageId}`
        : `https://mail.google.com/mail/u/0/#all/${threadId}`
      : null;
    
    const chipContent = (
      <Chip
        label={s.label}
        size="small"
        component={gmailLink ? 'a' : 'div'}
        href={gmailLink || undefined}
        target={gmailLink ? '_blank' : undefined}
        rel={gmailLink ? 'noopener noreferrer' : undefined}
        onClick={gmailLink ? (e: React.MouseEvent) => {
          e.stopPropagation();
          window.open(gmailLink, '_blank');
        } : undefined}
        className={isBlinking ? 'inquiry-reply-blink' : ''}
        sx={{
          bgcolor: s.bg,
          color: textColor,
          borderRadius: '4px',
          minHeight: 28,
          height: 28,
          cursor: gmailLink ? 'pointer' : 'default',
          textDecoration: 'none',
          // Używamy globalnej animacji inquiry-reply-blink dla synchronizacji wszystkich statusów "Odpowiedź"
          // Animacja jest zdefiniowana w index.css jako klasa .inquiry-reply-blink
          '&:hover': gmailLink ? {
            bgcolor: '#FFB3BA', // Jaśniejszy różowy przy najechaniu
            textDecoration: 'none'
          } : {},
          '& .MuiChip-label': {
            px: 1,
            py: 0,
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '16px',
            color: textColor
          }
        }}
      />
    );
    
    return chipContent;
  };

  const convertToUIFormat = (inquiry: OfferStandaloneInquiryDTO, pkgs: OfferStandalonePackageDTO[]): InquiryUI => {
    const pkgName = pkgs.find(p => p.package_id === inquiry.package_id)?.name || '';
    const inquiryAny = inquiry as any;
    return {
      id: inquiry.inquiry_id,
      packageId: inquiry.package_id || '',
      packageName: pkgName,
      contractorEmail: inquiry.contractor_email,
      contractorPhone1: inquiry.contractor_phone_1,
      contractorPhone2: inquiry.contractor_phone_2,
      subject: inquiry.subject,
      status: statusToUI(inquiry.status),
      createdAt: inquiry.created_at || new Date().toISOString(),
      notes: inquiry.notes,
      lastContactDate: inquiry.last_contact_date,
      plannedContactDate: inquiry.planned_contact_date,
      gmailDraftId: inquiry.gmail_draft_id,
      gmailMessageId: inquiry.gmail_message_id,
      createdByName: inquiryAny.created_by_name || null,
      createdByAvatarUrl: inquiryAny.created_by_avatar_url || null,
      inviteToCollectifileBoard: !!inquiryAny.invite_to_collectifile_board
    };
  };

  const loadData = (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    console.log('InquiriesTab - loadData called for offerId:', offerId);
    Promise.all([
      offerStandaloneManageService.getPackages(offerId),
      offerStandaloneManageService.getInquiries(offerId),
      offerStandaloneManageService.getReceivedOffers(offerId)
    ])
      .then(([pkgs, inqs, roffers]) => {
        console.log('InquiriesTab - pkgs:', pkgs);
        console.log('InquiriesTab - inqs:', inqs);
        console.log('InquiriesTab - inqs type:', typeof inqs, 'isArray:', Array.isArray(inqs));
        if (Array.isArray(inqs) && inqs.length > 0) {
          console.log('InquiriesTab - first inquiry:', inqs[0]);
          console.log('InquiriesTab - first inquiry created_by_name:', (inqs[0] as any).created_by_name);
          console.log('InquiriesTab - first inquiry created_by_avatar_url:', (inqs[0] as any).created_by_avatar_url);
        } else {
          console.warn('InquiriesTab - inqs is empty or not an array:', inqs);
        }
        const packagesArray = Array.isArray(pkgs) ? pkgs : [];
        const inquiriesArray = Array.isArray(inqs) ? inqs : [];
        const receivedOffersArray = Array.isArray(roffers) ? roffers : [];
        console.log('InquiriesTab - setting packages:', packagesArray.length);
        console.log('InquiriesTab - setting inquiries:', inquiriesArray.length);
        console.log('InquiriesTab - setting receivedOffers:', receivedOffersArray.length);
        setPackages(packagesArray);
        setInquiries(inquiriesArray);
        setReceivedOffers(receivedOffersArray);
        const uiData = inquiriesArray.map(i => convertToUIFormat(i, packagesArray));
        console.log('InquiriesTab - setting inquiriesUI:', uiData.length);
        setInquiriesUI(uiData);
      })
      .catch(err => {
        console.error('InquiriesTab - loadData error:', err);
        setError(err.message);
        showNotification(`Błąd: ${err.message}`, 'error');
      })
      .finally(() => {
        console.log('InquiriesTab - loadData finished');
        if (!silent) {
          setLoading(false);
        }
      });
  };

  useEffect(() => { loadData(); }, [offerId]);

  const handleOpenInquiryForm = () => { setEditMode(false); setSelectedInquiry(null); setOpenInquiryForm(true); };
  const handleCloseInquiryForm = () => { setOpenInquiryForm(false); setEditMode(false); setSelectedInquiry(null); };
  const handleInquiryActionClose = () => { setInquiryAnchorEl(null); };

  const handleAddInquiry = (newInquiryUI: InquiryUI) => {
    offerStandaloneManageService.createInquiry(offerId, {
      package_id: newInquiryUI.packageId || undefined,
      contractor_email: newInquiryUI.contractorEmail,
      contractor_phone_1: newInquiryUI.contractorPhone1?.trim() || undefined,
      contractor_phone_2: newInquiryUI.contractorPhone2?.trim() || undefined,
      subject: newInquiryUI.subject,
      status: 'Przygotowane',
      invite_to_collectifile_board: newInquiryUI.inviteToCollectifileBoard || false
    })
      .then(() => { showNotification('Zapytanie zostało utworzone pomyślnie', 'success'); loadData(); onRefreshData && onRefreshData(); })
      .catch(err => showNotification(err.response?.data?.message || 'Nie udało się utworzyć zapytania.', 'error'));
  };

  const handleEditInquiry = (editedInquiryUI: InquiryUI) => {
    offerStandaloneManageService.updateInquiry(offerId, editedInquiryUI.id, {
      package_id: editedInquiryUI.packageId || undefined,
      contractor_email: editedInquiryUI.contractorEmail,
      contractor_phone_1: editedInquiryUI.contractorPhone1?.trim() || undefined,
      contractor_phone_2: editedInquiryUI.contractorPhone2?.trim() || undefined,
      subject: editedInquiryUI.subject,
      invite_to_collectifile_board: editedInquiryUI.inviteToCollectifileBoard
    })
      .then(() => { showNotification('Zapisano zmiany zapytania.', 'success'); loadData(); onRefreshData && onRefreshData(); })
      .catch(() => showNotification('Nie udało się zapisać zmian zapytania.', 'error'));
  };

  const handleDeleteInquiry = (inquiryId: string) => {
    offerStandaloneManageService.deleteInquiry(offerId, inquiryId)
      .then(() => {
        showNotification('Zapytanie zostało usunięte pomyślnie.', 'success');
        loadData();
        onRefreshData && onRefreshData();
      })
      .catch((err) => {
        showNotification(err.response?.data?.message || 'Wystąpił błąd podczas usuwania zapytania.', 'error');
      });
  };

  const handleCreateGmailDraft = async (inquiryId: string) => {
    try {
      const res = await offerStandaloneManageService.createInquiryGmailDraft(offerId, inquiryId);
      if (res?.requireReauth) showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie, aby udzielić zgody.', 'error');
      else showNotification('Wersja robocza e-maila utworzona w Gmailu.', 'success');
      loadData({ silent: true });
    } catch (e: any) {
      showNotification(e?.response?.data?.message || 'Nie udało się utworzyć draftu Gmail.', 'error');
    } finally { handleInquiryActionClose(); }
  };

  const handleSend = async (inquiry: InquiryUI) => {
    try {
      if (inquiry.status === 'Przygotowane') {
        const res = await offerStandaloneManageService.sendInquiryDirectEmail(offerId, inquiry.id);
        if (res?.requireReauth) showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie, aby wysłać wiadomość.', 'error');
        else showNotification('Wiadomość wysłana.', 'success');
      } else if (inquiry.status === 'draft gmail') {
        const res = await offerStandaloneManageService.sendInquiryGmailDraft(offerId, inquiry.id);
        if (res?.requireReauth) showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie, aby wysłać wiadomość z draftu.', 'error');
        else showNotification('Wiadomość wysłana z draftu.', 'success');
      }
      loadData({ silent: true });
    } catch (e: any) {
      showNotification(e?.response?.data?.message || 'Nie udało się wysłać wiadomości.', 'error');
    } finally { handleInquiryActionClose(); }
  };

  const handleSetStatusOdmowiono = async (inquiryId: string) => {
    try {
      // Zapisz poprzedni status przed zmianą
      const inquiry = inquiries.find(i => i.inquiry_id === inquiryId);
      if (inquiry && inquiry.status !== 'Odmówiono') {
        setPreviousStatuses(prev => ({
          ...prev,
          [inquiryId]: inquiry.status
        }));
      }
      
      await offerStandaloneManageService.updateInquiry(offerId, inquiryId, {
        status: 'Odmówiono'
      });
      showNotification('Status zapytania zmieniony na "Odmówiono".', 'success');
      loadData({ silent: true });
      onRefreshData && onRefreshData();
    } catch (e: any) {
      showNotification(e?.response?.data?.message || 'Nie udało się zmienić statusu zapytania.', 'error');
    } finally { handleInquiryActionClose(); }
  };

  const handleRestorePreviousStatus = async (inquiryId: string) => {
    try {
      let previousStatus = previousStatuses[inquiryId];
      
      // Jeśli nie ma zapisanego poprzedniego statusu, spróbuj określić go na podstawie innych pól
      if (!previousStatus) {
        const inquiry = inquiries.find(i => i.inquiry_id === inquiryId);
        if (inquiry) {
          // Logika określania poprzedniego statusu na podstawie pól
          if (inquiry.gmail_reply_message_id || inquiry.status === 'Odpowiedź') {
            previousStatus = 'Odpowiedź';
          } else if (inquiry.gmail_message_id || inquiry.status === 'Wysłane') {
            previousStatus = 'Wysłane';
          } else if (inquiry.gmail_draft_id || inquiry.status === 'draft gmail') {
            previousStatus = 'draft gmail';
          } else {
            previousStatus = 'Przygotowane';
          }
        } else {
          showNotification('Nie można określić poprzedniego statusu.', 'warning');
          return;
        }
      }
      
      await offerStandaloneManageService.updateInquiry(offerId, inquiryId, {
        status: previousStatus as any
      });
      
      // Usuń zapisany poprzedni status
      setPreviousStatuses(prev => {
        const newPrev = { ...prev };
        delete newPrev[inquiryId];
        return newPrev;
      });
      
      showNotification(`Status zapytania przywrócony do "${previousStatus}".`, 'success');
      loadData({ silent: true });
      onRefreshData && onRefreshData();
    } catch (e: any) {
      showNotification(e?.response?.data?.message || 'Nie udało się przywrócić poprzedniego statusu.', 'error');
    } finally { handleInquiryActionClose(); }
  };

  const handleCheckReplies = async (inquiryId: string) => {
    try {
      const res = await offerStandaloneManageService.checkRepliesForInquiry(offerId, inquiryId);
      if (res?.requireReauth) {
        showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie, aby sprawdzić odpowiedzi.', 'error');
      } else {
        const repliesCount = res?.data?.replies?.length || 0;
        if (repliesCount > 0) {
          showNotification(`Znaleziono ${repliesCount} odpowiedzi.`, 'success');
        } else {
          showNotification('Nie znaleziono odpowiedzi w wątku Gmail.', 'info');
        }
      }
      loadData({ silent: true });
      onRefreshData && onRefreshData();
    } catch (e: any) {
      showNotification(e?.response?.data?.message || 'Nie udało się sprawdzić odpowiedzi.', 'error');
    } finally { handleInquiryActionClose(); }
  };

  const handleAddReceivedOffer = () => {
    if (actionInquiryIndex >= 0 && filteredInquiries[actionInquiryIndex]) {
      const inquiry = filteredInquiries[actionInquiryIndex];
      const originalInquiry = inquiries.find(i => i.inquiry_id === inquiry.id);
      if (originalInquiry && onOpenAddReceivedOffer) {
        onOpenAddReceivedOffer({
          inquiryId: originalInquiry.inquiry_id,
          contractorEmail: originalInquiry.contractor_email,
          packageId: originalInquiry.package_id || undefined
        });
      }
      handleInquiryActionClose();
    }
  };

  // Sortowanie
  type SortKey = 'id' | 'kp' | 'package' | 'contractor' | 'subject' | 'status' | 'created';
  const [orderBy, setOrderBy] = React.useState<SortKey>('id');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  
  const handleRequestSort = (property: SortKey) => {
    if (orderBy === property) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(property);
      setOrder('asc');
    }
  };

  const StrongSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#2D2D32' }} />);
  const MutedSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#d4d4d9' }} />);
  
  const getSortLabelProps = (keyId: SortKey) => {
    const isActive = orderBy === keyId;
    return {
      IconComponent: (isActive ? StrongSortIcon : MutedSortIcon) as any,
      active: isActive,
      direction: isActive ? order : 'asc' as 'asc' | 'desc',
      hideSortIcon: false,
      onClick: () => handleRequestSort(keyId),
      sx: {
        '& .MuiTableSortLabel-icon': {
          color: isActive ? '#2D2D32' : '#d4d4d9'
        }
      }
    };
  };

  const sortedInquiries = React.useMemo(() => {
    const arr = [...inquiriesUI];
    const dir = order === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (orderBy) {
        case 'id': {
          return String(a.id || '').localeCompare(String(b.id || '')) * dir;
        }
        case 'kp': {
          const av = String(a.createdByName || '');
          const bv = String(b.createdByName || '');
          return av.localeCompare(bv) * dir;
        }
        case 'package': {
          return String(a.packageName || '').localeCompare(String(b.packageName || '')) * dir;
        }
        case 'contractor': {
          return String(a.contractorEmail || '').localeCompare(String(b.contractorEmail || '')) * dir;
        }
        case 'subject': {
          return String(a.subject || '').localeCompare(String(b.subject || '')) * dir;
        }
        case 'status': {
          return String(a.status || '').localeCompare(String(b.status || '')) * dir;
        }
        case 'created': {
          const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return (aDate - bDate) * dir;
        }
        default:
          return 0;
      }
    });
    return arr;
  }, [inquiriesUI, orderBy, order]);

  const filteredInquiries = React.useMemo(() => {
    let filtered = sortedInquiries;
    
    // Filtr po statusie
    if (statusFilter) {
      filtered = filtered.filter(inq => {
        const originalInquiry = inquiries.find(i => i.inquiry_id === inq.id);
        const hasLinkedOffer = receivedOffers.some(ro => ro.inquiry_id === inq.id);
        
        // Określ status do wyświetlenia (zgodnie z logiką renderStatusChip)
        let displayStatus: string = inq.status;
        if (hasLinkedOffer) {
          displayStatus = 'Oferta';
        } else if (inq.status === 'Odpowiedź' || originalInquiry?.status === 'Odpowiedź') {
          displayStatus = 'Odpowiedź';
        } else if (originalInquiry?.status === 'Wysłane') {
          displayStatus = 'Wysłane';
        }
        
        return displayStatus === statusFilter;
      });
    }
    
    return filtered;
  }, [sortedInquiries, statusFilter, inquiries, receivedOffers]);

  return (
    <Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
      ) : error ? (
        <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, color: 'error.dark' }}><Typography>{error}</Typography></Box>
      ) : (
        <Box sx={{ position: 'relative', width: '100%' }}>
          {/* Pasek akcji z filtrem – nad tabelą, wyrównany do prawej */}
          <Box sx={{ position: 'absolute', top: -36, right: 0, display: 'flex', alignItems: 'center', zIndex: 3 }}>
            {/* Filtr statusów */}
            <Select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return 'Status';
                const statusMap: Record<string, { bg: string; label: string }> = {
                  'Przygotowane': { bg: '#E3F2FD', label: 'Przygotowane' },
                  'draft gmail': { bg: '#FFF8E1', label: 'Draft Gmail' },
                  'Wysłane': { bg: '#E8F5E9', label: 'Wysłane' },
                  'Odpowiedź': { bg: '#FFCDD2', label: 'Odpowiedź' },
                  'Oferta': { bg: '#E1BEE7', label: 'Oferta' },
                  'Odmówiono': { bg: '#FFE5CC', label: 'Odmówiono' },
                };
                const s = statusMap[selected] || { bg: '#f5f5f5', label: selected };
                return (
                  <Chip
                    label={s.label}
                    size="small"
                    sx={{
                      height: 20,
                      backgroundColor: s.bg,
                      color: '#020242',
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '12px',
                      '& .MuiChip-label': {
                        px: 1,
                        py: 0
                      }
                    }}
                  />
                );
              }}
              IconComponent={(iconProps: any) => (
                <Box
                  component="img"
                  alt="dropdown"
                  src="/assets/filters/keyboard_arrow_down.png"
                  {...iconProps}
                  sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
                />
              )}
              sx={{ 
                height: 29,
                width: 172,
                pr: '8px',
                '& .MuiSelect-select': {
                  display: 'flex', alignItems: 'center', height: 29,
                  fontFamily: 'Roboto, sans-serif', fontSize: '14px',
                  py: 0,
                  lineHeight: '15px'
                },
                backgroundColor: '#fff',
                borderRadius: '4px',
                '& fieldset': { borderColor: '#d4d4d9' },
                '&:hover fieldset': { borderColor: '#595966' },
                '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
                '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
              }}
            >
              <MenuItem value="">
                <Box sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#B9B9B9' }}>Wszystkie</Box>
              </MenuItem>
              <MenuItem value="Przygotowane">
                <Chip
                  label="Przygotowane"
                  size="small"
                  sx={{
                    height: 20,
                    backgroundColor: '#E3F2FD',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '12px',
                    '& .MuiChip-label': {
                      px: 1,
                      py: 0
                    }
                  }}
                />
              </MenuItem>
              <MenuItem value="draft gmail">
                <Chip
                  label="Draft Gmail"
                  size="small"
                  sx={{
                    height: 20,
                    backgroundColor: '#FFF8E1',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '12px',
                    '& .MuiChip-label': {
                      px: 1,
                      py: 0
                    }
                  }}
                />
              </MenuItem>
              <MenuItem value="Wysłane">
                <Chip
                  label="Wysłane"
                  size="small"
                  sx={{
                    height: 20,
                    backgroundColor: '#E8F5E9',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '12px',
                    '& .MuiChip-label': {
                      px: 1,
                      py: 0
                    }
                  }}
                />
              </MenuItem>
              <MenuItem value="Odpowiedź">
                <Chip
                  label="Odpowiedź"
                  size="small"
                  sx={{
                    height: 20,
                    backgroundColor: '#FFCDD2',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '12px',
                    '& .MuiChip-label': {
                      px: 1,
                      py: 0
                    }
                  }}
                />
              </MenuItem>
              <MenuItem value="Oferta">
                <Chip
                  label="Oferta"
                  size="small"
                  sx={{
                    height: 20,
                    backgroundColor: '#E1BEE7',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '12px',
                    '& .MuiChip-label': {
                      px: 1,
                      py: 0
                    }
                  }}
                />
              </MenuItem>
              <MenuItem value="Odmówiono">
                <Chip
                  label="Odmówiono"
                  size="small"
                  sx={{
                    height: 20,
                    backgroundColor: '#FFE5CC',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '12px',
                    '& .MuiChip-label': {
                      px: 1,
                      py: 0
                    }
                  }}
                />
              </MenuItem>
            </Select>
            {/* Odstęp między filtrem a grupą akcji */}
            <Box sx={{ width: 16 }} />
            {/* Grupa akcji */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Operacje na wielu zapytaniach">
                <span>
                  <IconButton onClick={() => setOpenBulkActionsDrawer(true)}>
                    <Box component="img" src="/assets/icons/playlist_add_check.svg" alt="Operacje na wielu" sx={{ width: 22, height: 22 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Dodaj zapytanie">
                <span>
                  <IconButton onClick={handleOpenInquiryForm} data-hotkey-add="true">
                    <Box component="img" src="/assets/icons/add.svg" alt="Dodaj" sx={{ width: 22, height: 22 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            {/* Odstęp 16px między grupami ikon */}
            <Box sx={{ width: 16 }} />
            {/* Dostosuj kolumny – ostatnia ikona, na prawej krawędzi */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '4px',
                  p: 0,
                  alignSelf: 'flex-end',
                  bgcolor: colResizeMode ? '#E0E0F5' : 'transparent',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
                }}
                onClick={() => { 
                  setDraftColumnsVisible({ ...columnsVisible });
                  setDraftOrder([...columnOrder]);
                  setDraftWidths({ ...columnWidths });
                  setColResizeMode(true); 
                  setColsOpen(true); 
                  setColsPosition({ top: 0, left: 0 }); 
                }}
              >
                <Box
                  component="img"
                  alt="kolumny"
                  src={'/assets/icons/frame-344.svg?v=2'}
                  sx={{ width: 32, height: 32 }}
                />
              </IconButton>
            </Box>
          </Box>

          <TableContainer 
            component={Paper} 
            sx={{ 
              width: '100%',
              mt: 0,
              height: '455px',
              overflow: 'auto',
              position: 'relative',
              border: '1px solid #d0d0d6',
              borderBottom: '1px solid #d0d0d6',
              borderRadius: 0,
              borderTopRightRadius: 4,
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4,
              overflowX: 'hidden',
              overflowY: 'auto',
              boxShadow: 'none',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { width: 0, height: 0 }
            }}
          >
            <Table 
              size="small"
              stickyHeader
              sx={{
                tableLayout: 'fixed',
                width: '100%',
                '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
                '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
                '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
                '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
              }}
            >
              {(() => {
                const ord = (draftOrder ?? columnOrder);
                return (
                  <colgroup>
                    {ord.filter((k)=>k!=='actions').map((key) => (
                      colV(key) ? <col key={`col-${key}`} style={{ width: getColW(key) }} /> : null
                    ))}
                    {colV('actions') ? <col key="col-actions" style={{ width: getColW('actions') }} /> : null}
                  </colgroup>
                );
              })()}
              <TableHead>
                <TableRow>
                  {(draftOrder ?? columnOrder).filter((k)=>k !== 'actions').map((key) => colV(key) ? renderResizableTh(key, (
                    key === 'id' ? 'ID' :
                    key === 'kp' ? 'U' :
                    key === 'package' ? 'Pakiet' :
                    key === 'contractor' ? 'Kontrahent' :
                    key === 'subject' ? 'Temat' :
                    key === 'status' ? 'Status' :
                    key === 'created' ? 'Utworzone' :
                    key === 'last_contact' ? 'Ostatni kontakt' :
                    key === 'planned_contact' ? 'Planowany kontakt' :
                    key === 'notes' ? 'Notatka' :
                    ''
                  ), key === 'kp' ? 'center' : undefined, key !== 'actions', key === 'kp' ? 'Utworzył/a' : undefined) : null)}
                  {colV('actions') ? renderResizableTh('actions', '', 'center') : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInquiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={(draftOrder ?? columnOrder).filter((k)=>colV(k)).length} align="center">
                      <Typography variant="body2" sx={{ py: 2, fontFamily: 'Roboto, sans-serif', color: '#2D2D32' }}>
                        Brak zapytań
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInquiries.map((inquiry) => {
                    const ord = (draftOrder ?? columnOrder).filter((k)=>k !== 'actions');
                    return (
                      <TableRow 
                        key={inquiry.id} 
                        hover
                        sx={{ 
                          '&:hover': { backgroundColor: '#F5F5FF !important' },
                          '&.MuiTableRow-hover:hover': { backgroundColor: '#F5F5FF !important' }
                        }}
                      >
                        {ord.map((key) => {
                          if (!colV(key)) return null;
                          if (key === 'id') {
                            return (
                              <TableCell key={key} sx={{ width: getColW('id') }}>
                                <Box sx={{ pl: '16px' }}>{inquiry.id}</Box>
                              </TableCell>
                            );
                          }
                          if (key === 'kp') {
                            return (
                              <TableCell key={key} align="center" sx={{ width: getColW('kp') }}>
                                {(() => {
                                  const avatarUrl = inquiry.createdByAvatarUrl;
                                  const userName = inquiry.createdByName;
                                  
                                  // Sprawdź czy mamy dane użytkownika (nie null, nie undefined, nie pusty string)
                                  const hasAvatarUrl = avatarUrl && typeof avatarUrl === 'string' && avatarUrl.trim().length > 0;
                                  const hasUserName = userName && typeof userName === 'string' && userName.trim().length > 0;
                                  const hasUser = hasAvatarUrl || hasUserName;
                                  
                                  if (hasUser) {
                                    return (
                                      <Tooltip title={userName || ''} arrow>
                                        <Avatar src={hasAvatarUrl ? avatarUrl : undefined} sx={{ width: 25, height: 25, fontSize: 12 }}>
                                          {hasUserName ? userName.substring(0, 1).toUpperCase() : '?'}
                                        </Avatar>
                                      </Tooltip>
                                    );
                                  } else {
                                    return (
                                      <Tooltip title="AI" arrow>
                                        <Avatar sx={{ width: 25, height: 25, fontSize: 12, bgcolor: 'transparent', p: 0 }}>
                                          <Box component="img" src="/assets/ai_badge_unitresco.svg" alt="AI" sx={{ width: 25, height: 25 }} />
                                        </Avatar>
                                      </Tooltip>
                                    );
                                  }
                                })()}
                              </TableCell>
                            );
                          }
                          if (key === 'package') {
                            return (
                              <TableCell key={key} sx={{ width: getColW('package') }}>{inquiry.packageName}</TableCell>
                            );
                          }
                          if (key === 'contractor') {
                            const originalInquiry = inquiries.find(i => i.inquiry_id === inquiry.id);
                            const phone1 = originalInquiry?.contractor_phone_1 || '';
                            const phone2 = originalInquiry?.contractor_phone_2 || '';
                            
                            // Funkcja do czyszczenia numeru telefonu (usunięcie spacji, myślników, nawiasów itp.)
                            const cleanPhoneNumber = (phone: string): string => {
                              return phone.replace(/[\s\-\(\)\+]/g, '');
                            };
                            
                            // Funkcja do kopiowania numeru telefonu
                            const copyPhoneNumber = async (phone: string, e: React.MouseEvent) => {
                              e.stopPropagation();
                              const cleaned = cleanPhoneNumber(phone);
                              try {
                                await navigator.clipboard.writeText(cleaned);
                                showNotification(`Skopiowano: ${cleaned}`, 'success');
                              } catch (err) {
                                console.error('Błąd kopiowania:', err);
                                showNotification('Nie udało się skopiować numeru telefonu', 'error');
                              }
                            };
                            
                            return (
                              <TableCell key={key} sx={{ width: getColW('contractor') }}>
                                <Tooltip
                                  title={
                                    <Box sx={{ p: 1 }}>
                                      <Box sx={{ mb: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#fff', minWidth: '80px' }}>
                                            Nr telefonu 1:
                                          </Typography>
                                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#fff', flex: 1 }}>
                                            {phone1 || '-'}
                                          </Typography>
                                          {phone1 && (
                                            <IconButton
                                              size="small"
                                              onClick={(e) => copyPhoneNumber(phone1, e)}
                                              sx={{
                                                color: '#fff',
                                                padding: '4px',
                                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                                              }}
                                            >
                                              <ContentCopy sx={{ fontSize: '14px' }} />
                                            </IconButton>
                                          )}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#fff', minWidth: '80px' }}>
                                            Nr telefonu 2:
                                          </Typography>
                                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#fff', flex: 1 }}>
                                            {phone2 || '-'}
                                          </Typography>
                                          {phone2 && (
                                            <IconButton
                                              size="small"
                                              onClick={(e) => copyPhoneNumber(phone2, e)}
                                              sx={{
                                                color: '#fff',
                                                padding: '4px',
                                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                                              }}
                                            >
                                              <ContentCopy sx={{ fontSize: '14px' }} />
                                            </IconButton>
                                          )}
                                        </Box>
                                      </Box>
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Box
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedContractorEmail(inquiry.contractorEmail);
                                      setSelectedInquiryDTO(originalInquiry || null);
                                      setSelectedPackageName(inquiry.packageName || '');
                                      setOpenSubcontractorDrawer(true);
                                    }}
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitBoxOrient: 'vertical',
                                      WebkitLineClamp: 2,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                      color: '#2D2D32',
                                      cursor: 'pointer',
                                      fontFamily: 'Roboto, sans-serif',
                                      '&:hover': {
                                        color: '#1976D2'
                                      }
                                    }}
                                  >
                                    {inquiry.contractorEmail}
                                  </Box>
                                </Tooltip>
                              </TableCell>
                            );
                          }
                          if (key === 'subject') {
                            return (
                              <TableCell key={key} sx={{ width: getColW('subject') }}>{inquiry.subject}</TableCell>
                            );
                          }
                          if (key === 'status') {
                            return (
                              <TableCell key={key} sx={{ width: getColW('status') }}>{renderStatusChip(inquiry.status, inquiry.id)}</TableCell>
                            );
                          }
                          if (key === 'created') {
                            const formatDate = (dateString: string) => {
                              const date = new Date(dateString);
                              const year = date.getFullYear();
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              return `${year}-${month}-${day}`;
                            };
                            return (
                              <TableCell key={key} sx={{ width: getColW('created') }}>{inquiry.createdAt ? formatDate(inquiry.createdAt) : '-'}</TableCell>
                            );
                          }
                          if (key === 'last_contact') {
                            const date = inquiry.lastContactDate || '-';
                            return (
                              <TableCell key={key} sx={{ width: getColW('last_contact') }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography sx={{ fontFamily: 'Roboto', fontSize: '0.85rem', color: '#2D2D32', flex: 1 }}>{date}</Typography>
                                  <IconButton
                                    size="small"
                                    sx={{ p: 0.25, color: '#595966' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      pendingDateUpdate.current = { inquiryId: inquiry.id, field: 'last_contact_date' };
                                      const input = dateInputRef.current;
                                      if (input) {
                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                        input.style.left = `${rect.left}px`;
                                        input.style.top = `${rect.bottom + 4}px`;
                                        input.style.width = '200px';
                                        input.style.height = '32px';
                                        input.style.pointerEvents = 'auto';
                                        input.value = inquiry.lastContactDate || '';
                                        try { (input as any).showPicker?.(); } catch {}
                                        input.click();
                                      }
                                    }}
                                  >
                                    <CalendarMonth sx={{ fontSize: '18px' }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            );
                          }
                          if (key === 'planned_contact') {
                            const date = inquiry.plannedContactDate || '-';
                            return (
                              <TableCell key={key} sx={{ width: getColW('planned_contact') }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography sx={{ fontFamily: 'Roboto', fontSize: '0.85rem', color: '#2D2D32', flex: 1 }}>{date}</Typography>
                                  <IconButton
                                    size="small"
                                    sx={{ p: 0.25, color: '#595966' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      pendingDateUpdate.current = { inquiryId: inquiry.id, field: 'planned_contact_date' };
                                      const input = dateInputRef.current;
                                      if (input) {
                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                        input.style.left = `${rect.left}px`;
                                        input.style.top = `${rect.bottom + 4}px`;
                                        input.style.width = '200px';
                                        input.style.height = '32px';
                                        input.style.pointerEvents = 'auto';
                                        input.value = inquiry.plannedContactDate || '';
                                        try { (input as any).showPicker?.(); } catch {}
                                        input.click();
                                      }
                                    }}
                                  >
                                    <CalendarMonth sx={{ fontSize: '18px' }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            );
                          }
                          if (key === 'notes') {
                            const notesText = inquiry.notes || '-';
                            return (
                              <TableCell key={key} sx={{ width: getColW('notes') }}>
                                <Typography
                                  sx={{
                                    fontFamily: 'Roboto', fontSize: '0.85rem', color: '#2D2D32',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {notesText}
                                </Typography>
                              </TableCell>
                            );
                          }
                          return null;
                        })}
                        {colV('actions') ? (
                          <TableCell align="center" sx={{ width: getColW('actions') }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                              <Tooltip title="Podgląd wiadomości" arrow>
                                <span>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setActionInquiryIndex(filteredInquiries.indexOf(inquiry)); setOpenEmailDialog(true); }} aria-label="podgląd wiadomości">
                                    <Visibility fontSize="small" sx={{ color: '#2D2D32' }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setActionInquiryIndex(filteredInquiries.indexOf(inquiry)); setInquiryAnchorEl(e.currentTarget); }} aria-label="więcej">
                                <MoreVert fontSize="small" sx={{ color: '#2D2D32' }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Okno „Dostosuj kolumny” – spójne z tabelą projektów */}
          <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }} onClick={()=> { 
            setColsOpen(false);
            setDraftColumnsVisible(null);
            setDraftOrder(null);
            setDraftWidths({});
          }}>
            <Paper
              elevation={8}
              sx={{
                position: 'fixed',
                bottom: 0,
                right: 0,
                height: `${COLUMNS_DIALOG.height}px`,
                width: `${COLUMNS_DIALOG.width}px`,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
                borderRadius: 0,
                zIndex: (theme) => theme.zIndex.modal + 2,
                backgroundColor: '#fff',
                top: colsPosition.top ? `${colsPosition.top}px` : undefined,
                left: colsPosition.left ? `${colsPosition.left}px` : undefined,
                pointerEvents: 'auto'
              }}
              onClick={(e)=> e.stopPropagation()}
            >
              <Box
                sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1, cursor: 'move', userSelect: 'none' }}
                onMouseDown={(e)=> setColsDragging({ startX: e.clientX, startY: e.clientY, top: colsPosition.top, left: colsPosition.left })}
              >
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
                <IconButton size="small" onClick={()=>{ 
                  setColsOpen(false); 
                  setColResizeMode(false); 
                  setDraftColumnsVisible(null);
                  setDraftOrder(null);
                  setDraftWidths({});
                }}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
              <Divider />
              <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
                {(() => {
                  const list = draftColumnsVisible ?? columnsVisible;
                  if (draftColumnsVisible == null) setDraftColumnsVisible({ ...columnsVisible });
                  const ord = draftOrder ?? columnOrder;
                  if (draftOrder == null) setDraftOrder([...columnOrder]);
                  return ord.map((key) => (
                    <Box key={key} sx={{ py: 1, px: 2 }}
                      draggable
                      onDragStart={(e)=>{ e.dataTransfer.setData('text/plain', key); }}
                      onDragOver={(e)=>{ e.preventDefault(); }}
                      onDrop={(e)=>{ e.preventDefault(); const src = e.dataTransfer.getData('text/plain'); if (!src || src===key) return; setDraftOrder(prev=>{ const cur = [...(prev||ord)]; const from = cur.indexOf(src); const to = cur.indexOf(key); if (from<0||to<0) return cur; cur.splice(to,0,cur.splice(from,1)[0]); return cur; }); }}
                    >
                      <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <IconButton onClick={()=>setDraftColumnsVisible(prev=>({ ...(prev||{}), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                            <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                          </IconButton>
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>{(key === 'id' ? 'ID' : key === 'package' ? 'Pakiet' : key === 'contractor' ? 'Kontrahent' : key === 'subject' ? 'Temat' : key === 'status' ? 'Status' : key === 'created' ? 'Utworzone' : key === 'last_contact' ? 'Ostatni kontakt' : key === 'planned_contact' ? 'Planowany kontakt' : key === 'notes' ? 'Notatka' : key.toUpperCase())}</Typography>
                        </Box>
                        <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                      </Box>
                    </Box>
                  ));
                })()}
              </Box>
              <Divider />
              <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
                <Button variant="text" onClick={()=>{ 
                  setColsOpen(false); 
                  setColResizeMode(false); 
                  setDraftColumnsVisible(null);
                  setDraftOrder(null);
                  setDraftWidths({});
                }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
                <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ 
                  if (draftColumnsVisible) { 
                    setColumnsVisible(draftColumnsVisible); 
                    try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(draftColumnsVisible)); } catch {} 
                  } 
                  if (draftOrder) { 
                    const normalized = draftOrder.filter((k)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!draftOrder.includes(k))); 
                    setColumnOrder(normalized); 
                    try { localStorage.setItem(ORDER_KEY, JSON.stringify(normalized)); } catch {} 
                  }
                  // Zapisz szerokości kolumn - użyj aktualnych szerokości (mogły być zmienione przez przeciąganie)
                  // Jeśli są zmiany w draftWidths, użyj ich, w przeciwnym razie użyj aktualnych columnWidths
                  const widthsToSave = { ...columnWidths };
                  const finalOrder = draftOrder ?? columnOrder;
                  finalOrder.forEach((key) => {
                    const draftW = draftWidths[key];
                    if (draftW && typeof draftW === 'number' && draftW >= 24) {
                      widthsToSave[key] = draftW;
                    }
                  });
                  // Zapisz wszystkie aktualne szerokości
                  setColumnWidths(widthsToSave);
                  try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(widthsToSave));
                  } catch {}
                  setColsOpen(false); 
                  setColResizeMode(false);
                  setDraftColumnsVisible(null);
                  setDraftOrder(null);
                  setDraftWidths({});
                }}>
                  Zapisz zmiany
                </Button>
              </Box>
            </Paper>
          </Backdrop>
        </Box>
      )}

      <Menu anchorEl={inquiryAnchorEl} open={Boolean(inquiryAnchorEl)} onClose={handleInquiryActionClose}>
        {actionInquiryIndex >= 0 && (
          <MenuItem onClick={() => { setEditMode(true); setSelectedInquiry(filteredInquiries[actionInquiryIndex]); setOpenInquiryForm(true); handleInquiryActionClose(); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="edytuj" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
          </MenuItem>
        )}
        {actionInquiryIndex >= 0 && filteredInquiries[actionInquiryIndex]?.status === 'Przygotowane' && (
          <MenuItem onClick={() => handleCreateGmailDraft(filteredInquiries[actionInquiryIndex].id)}>
            <ListItemIcon><GmailIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="draft gmail" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
          </MenuItem>
        )}
        {actionInquiryIndex >= 0 && (filteredInquiries[actionInquiryIndex]?.status === 'Przygotowane' || filteredInquiries[actionInquiryIndex]?.status === 'draft gmail') && (
          <MenuItem onClick={() => handleSend(filteredInquiries[actionInquiryIndex])}>
            <ListItemIcon><Send fontSize="small" /></ListItemIcon>
            <ListItemText primary="wyślij wiadomość" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
          </MenuItem>
        )}
        {actionInquiryIndex >= 0 && filteredInquiries[actionInquiryIndex]?.status === 'Wysłane' && inquiries.find(i => i.inquiry_id === filteredInquiries[actionInquiryIndex]?.id)?.gmail_thread_id && (
          <MenuItem onClick={() => handleCheckReplies(filteredInquiries[actionInquiryIndex].id)}>
            <ListItemIcon><Refresh fontSize="small" /></ListItemIcon>
            <ListItemText primary="sprawdź odpowiedzi" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
          </MenuItem>
        )}
        {actionInquiryIndex >= 0 && filteredInquiries[actionInquiryIndex]?.status !== 'Odmówiono' && (
          <MenuItem onClick={() => handleSetStatusOdmowiono(filteredInquiries[actionInquiryIndex].id)}>
            <ListItemIcon>
              <Box component="img" src="/assets/icons/block.svg" alt="Status: Odmówiono" sx={{ width: 20, height: 20 }} />
            </ListItemIcon>
            <ListItemText primary="Status: Odmówiono" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
          </MenuItem>
        )}
        {actionInquiryIndex >= 0 && filteredInquiries[actionInquiryIndex]?.status === 'Odmówiono' && (
          <MenuItem onClick={() => handleRestorePreviousStatus(filteredInquiries[actionInquiryIndex].id)}>
            <ListItemIcon>
              <Refresh fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Przywróć poprzedni status" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
          </MenuItem>
        )}
        {actionInquiryIndex >= 0 && onOpenAddReceivedOffer && (
          <MenuItem onClick={handleAddReceivedOffer}>
            <ListItemIcon>
              <Box component="img" src="/assets/icons/price_check.svg" alt="Dodaj ofertę" sx={{ width: 20, height: 20 }} />
            </ListItemIcon>
            <ListItemText primary="dodaj ofertę" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
          </MenuItem>
        )}
        {actionInquiryIndex >= 0 && (
          <MenuItem onClick={() => {
            const inquiry = inquiries.find(i => i.inquiry_id === filteredInquiries[actionInquiryIndex]?.id);
            if (inquiry) {
              setSelectedInquiryForNotes(inquiry);
              setOpenNotesDrawer(true);
            }
            handleInquiryActionClose();
          }}>
            <ListItemIcon>
              <Box component="img" src="/assets/icons/notes_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg" alt="Notatki" sx={{ width: 20, height: 20 }} />
            </ListItemIcon>
            <ListItemText primary="Notatki" primaryTypographyProps={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 14, color: '#2D2D32' }} />
          </MenuItem>
        )}
      </Menu>

      {actionInquiryIndex >= 0 && (
        <OfferInquiryEmailDialog
          open={openEmailDialog}
          onClose={() => setOpenEmailDialog(false)}
          offerId={offerId}
          inquiry={inquiries.find(i => i.inquiry_id === filteredInquiries[actionInquiryIndex]?.id) || null}
          onUpdated={() => { loadData({ silent: true }); onRefreshData && onRefreshData(); }}
        />
      )}

      {openInquiryForm && (
        <AddInquiryForm
          open={openInquiryForm}
          onClose={handleCloseInquiryForm}
          packages={packages}
          initialPackage={initialPackage}
          editMode={editMode}
          inquiryData={selectedInquiry}
          projectName={projectName}
          offerDisplayId={offerDisplayId}
          hasCollectifileBoard={hasCollectifileBoard}
          onAdd={handleAddInquiry}
          onEdit={handleEditInquiry}
          onDelete={handleDeleteInquiry}
        />
      )}

      {/* Drawer operacji na wielu zapytaniach */}
      <Drawer
        anchor="right"
        open={openBulkActionsDrawer}
        onClose={() => setOpenBulkActionsDrawer(false)}
        PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0, top: '52px', height: 'calc(100vh - 52px)' } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #EDEDED', flexShrink: 0 }}>
            <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
              Operacje na wielu zapytaniach
            </Typography>
            <IconButton 
              onClick={() => setOpenBulkActionsDrawer(false)} 
              size="small"
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 30,
                minHeight: 30,
                '& > *': {
                  pointerEvents: 'none',
                },
              }}
            >
              <Box 
                component="img" 
                src="/assets/close.svg" 
                alt="Zamknij" 
                sx={{ 
                  width: 13, 
                  height: 13, 
                  display: 'block',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                }} 
              />
            </IconButton>
          </Box>
          <Box sx={{ p: 4, pt: 3, flex: 1, overflowY: 'auto' }}>
            <Box sx={{ display: 'grid', gap: 2 }}>
              {/* Opcja 1: Wygeneruj drafty wszystkich zapytań ze statusem Przygotowane */}
              <Button
                variant="outlined"
                fullWidth
                onClick={async () => {
                  const preparedInquiries = inquiries.filter(i => i.status === 'Przygotowane');
                  if (preparedInquiries.length === 0) {
                    showNotification('Brak zapytań ze statusem Przygotowane', 'info');
                    return;
                  }
                  setBulkActionLoading(true);
                  try {
                    let successCount = 0;
                    let errorCount = 0;
                    for (const inquiry of preparedInquiries) {
                      try {
                        const res = await offerStandaloneManageService.createInquiryGmailDraft(offerId, inquiry.inquiry_id);
                        if (res?.requireReauth) {
                          errorCount++;
                          showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie.', 'error');
                          break;
                        } else {
                          successCount++;
                        }
                      } catch (e: any) {
                        errorCount++;
                        console.error('Błąd tworzenia draftu dla zapytania', inquiry.inquiry_id, e);
                      }
                    }
                    if (successCount > 0) {
                      showNotification(`Utworzono ${successCount} draft${successCount === 1 ? '' : successCount < 5 ? 'y' : 'ów'} Gmail`, 'success');
                    }
                    if (errorCount > 0 && successCount === 0) {
                      showNotification('Nie udało się utworzyć draftów', 'error');
                    }
                    loadData();
                    onRefreshData && onRefreshData();
                  } finally {
                    setBulkActionLoading(false);
                    setOpenBulkActionsDrawer(false);
                  }
                }}
                disabled={bulkActionLoading}
                sx={{
                  height: 56,
                  borderRadius: '4px',
                  borderColor: '#d4d4d9',
                  color: '#2D2D32',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  px: 3,
                  '&:hover': {
                    borderColor: '#595966',
                    backgroundColor: '#F9F9F9'
                  },
                  '&:disabled': {
                    borderColor: '#d4d4d9',
                    color: '#B9B9B9'
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', fontWeight: 400, color: 'inherit' }}>
                    Wygeneruj drafty wszystkich zapytań ze statusem Przygotowane
                  </Typography>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966', mt: 0.5 }}>
                    {inquiries.filter(i => i.status === 'Przygotowane').length} zapytanie{inquiries.filter(i => i.status === 'Przygotowane').length !== 1 ? 'ń' : ''} do przetworzenia
                  </Typography>
                </Box>
              </Button>

              {/* Opcja 2: Wyślij wszystkie zapytania ze statusem Draft Gmail */}
              <Button
                variant="outlined"
                fullWidth
                onClick={async () => {
                  const draftInquiries = inquiries.filter(i => i.status === 'draft gmail');
                  if (draftInquiries.length === 0) {
                    showNotification('Brak zapytań ze statusem Draft Gmail', 'info');
                    return;
                  }
                  setBulkActionLoading(true);
                  try {
                    let successCount = 0;
                    let errorCount = 0;
                    for (const inquiry of draftInquiries) {
                      try {
                        const res = await offerStandaloneManageService.sendInquiryGmailDraft(offerId, inquiry.inquiry_id, inquiry.gmail_draft_id);
                        if (res?.requireReauth) {
                          errorCount++;
                          showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie.', 'error');
                          break;
                        } else {
                          successCount++;
                        }
                      } catch (e: any) {
                        errorCount++;
                        console.error('Błąd wysyłania draftu dla zapytania', inquiry.inquiry_id, e);
                      }
                    }
                    if (successCount > 0) {
                      showNotification(`Wysłano ${successCount} wiadomość${successCount === 1 ? '' : successCount < 5 ? 'i' : 'i'} z draftów Gmail`, 'success');
                    }
                    if (errorCount > 0 && successCount === 0) {
                      showNotification('Nie udało się wysłać wiadomości', 'error');
                    }
                    loadData();
                    onRefreshData && onRefreshData();
                  } finally {
                    setBulkActionLoading(false);
                    setOpenBulkActionsDrawer(false);
                  }
                }}
                disabled={bulkActionLoading}
                sx={{
                  height: 56,
                  borderRadius: '4px',
                  borderColor: '#d4d4d9',
                  color: '#2D2D32',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  px: 3,
                  '&:hover': {
                    borderColor: '#595966',
                    backgroundColor: '#F9F9F9'
                  },
                  '&:disabled': {
                    borderColor: '#d4d4d9',
                    color: '#B9B9B9'
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', fontWeight: 400, color: 'inherit' }}>
                    Wyślij wszystkie zapytania ze statusem Draft Gmail
                  </Typography>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966', mt: 0.5 }}>
                    {inquiries.filter(i => i.status === 'draft gmail').length} zapytanie{inquiries.filter(i => i.status === 'draft gmail').length !== 1 ? 'ń' : ''} do wysłania
                  </Typography>
                </Box>
              </Button>

              {/* Opcja 3: Wyślij wszystkie zapytania ze statusem Przygotowane */}
              <Button
                variant="outlined"
                fullWidth
                onClick={async () => {
                  const preparedInquiries = inquiries.filter(i => i.status === 'Przygotowane');
                  if (preparedInquiries.length === 0) {
                    showNotification('Brak zapytań ze statusem Przygotowane', 'info');
                    return;
                  }
                  setBulkActionLoading(true);
                  try {
                    let successCount = 0;
                    let errorCount = 0;
                    for (const inquiry of preparedInquiries) {
                      try {
                        const res = await offerStandaloneManageService.sendInquiryDirectEmail(offerId, inquiry.inquiry_id);
                        if (res?.requireReauth) {
                          errorCount++;
                          showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie.', 'error');
                          break;
                        } else {
                          successCount++;
                        }
                      } catch (e: any) {
                        errorCount++;
                        console.error('Błąd wysyłania wiadomości dla zapytania', inquiry.inquiry_id, e);
                      }
                    }
                    if (successCount > 0) {
                      showNotification(`Wysłano ${successCount} wiadomość${successCount === 1 ? '' : successCount < 5 ? 'i' : 'i'}`, 'success');
                    }
                    if (errorCount > 0 && successCount === 0) {
                      showNotification('Nie udało się wysłać wiadomości', 'error');
                    }
                    loadData();
                    onRefreshData && onRefreshData();
                  } finally {
                    setBulkActionLoading(false);
                    setOpenBulkActionsDrawer(false);
                  }
                }}
                disabled={bulkActionLoading}
                sx={{
                  height: 56,
                  borderRadius: '4px',
                  borderColor: '#d4d4d9',
                  color: '#2D2D32',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  px: 3,
                  '&:hover': {
                    borderColor: '#595966',
                    backgroundColor: '#F9F9F9'
                  },
                  '&:disabled': {
                    borderColor: '#d4d4d9',
                    color: '#B9B9B9'
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', fontWeight: 400, color: 'inherit' }}>
                    Wyślij wszystkie zapytania ze statusem Przygotowane
                  </Typography>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966', mt: 0.5 }}>
                    {inquiries.filter(i => i.status === 'Przygotowane').length} zapytanie{inquiries.filter(i => i.status === 'Przygotowane').length !== 1 ? 'ń' : ''} do wysłania
                  </Typography>
                </Box>
              </Button>

              {/* Opcja 4: Sprawdź odpowiedzi do zapytań ze statusem Wysłane */}
              <Button
                variant="outlined"
                fullWidth
                onClick={async () => {
                  const sentInquiries = inquiries.filter(i => i.status === 'Wysłane' && i.gmail_thread_id);
                  if (sentInquiries.length === 0) {
                    showNotification('Brak zapytań ze statusem Wysłane z wątkiem Gmail', 'info');
                    return;
                  }
                  setBulkActionLoading(true);
                  try {
                    let successCount = 0;
                    let errorCount = 0;
                    let repliesFoundCount = 0;
                    for (const inquiry of sentInquiries) {
                      try {
                        const res = await offerStandaloneManageService.checkRepliesForInquiry(offerId, inquiry.inquiry_id);
                        if (res?.requireReauth) {
                          errorCount++;
                          showNotification('Brak autoryzacji do Gmaila. Zaloguj się ponownie.', 'error');
                          break;
                        } else {
                          successCount++;
                          if (res?.data?.replies && res.data.replies.length > 0) {
                            repliesFoundCount += res.data.replies.length;
                          }
                        }
                      } catch (e: any) {
                        errorCount++;
                        console.error('Błąd sprawdzania odpowiedzi dla zapytania', inquiry.inquiry_id, e);
                      }
                    }
                    if (successCount > 0) {
                      if (repliesFoundCount > 0) {
                        showNotification(`Sprawdzono ${successCount} zapytanie${successCount === 1 ? '' : successCount < 5 ? 'ń' : 'ń'}, znaleziono ${repliesFoundCount} odpowiedzi.`, 'success');
                      } else {
                        showNotification(`Sprawdzono ${successCount} zapytanie${successCount === 1 ? '' : successCount < 5 ? 'ń' : 'ń'}, nie znaleziono odpowiedzi.`, 'info');
                      }
                    }
                    if (errorCount > 0 && successCount === 0) {
                      showNotification('Nie udało się sprawdzić odpowiedzi', 'error');
                    }
                    loadData();
                    onRefreshData && onRefreshData();
                  } finally {
                    setBulkActionLoading(false);
                    setOpenBulkActionsDrawer(false);
                  }
                }}
                disabled={bulkActionLoading}
                sx={{
                  height: 56,
                  borderRadius: '4px',
                  borderColor: '#d4d4d9',
                  color: '#2D2D32',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  px: 3,
                  '&:hover': {
                    borderColor: '#595966',
                    backgroundColor: '#F9F9F9'
                  },
                  '&:disabled': {
                    borderColor: '#d4d4d9',
                    color: '#B9B9B9'
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', fontWeight: 400, color: 'inherit' }}>
                    Sprawdź odpowiedzi do zapytań
                  </Typography>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966', mt: 0.5 }}>
                    {inquiries.filter(i => i.status === 'Wysłane' && i.gmail_thread_id).length} zapytanie{inquiries.filter(i => i.status === 'Wysłane' && i.gmail_thread_id).length !== 1 ? 'ń' : ''} do sprawdzenia
                  </Typography>
                </Box>
              </Button>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'flex-end', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)' }}>
            <Button
              onClick={() => setOpenBulkActionsDrawer(false)}
              variant="text"
              disabled={bulkActionLoading}
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                textTransform: 'none',
                color: '#2D2D32',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              Zamknij
            </Button>
          </Box>
        </Box>
      </Drawer>

      <SubcontractorDetailsDrawer
        open={openSubcontractorDrawer}
        onClose={() => {
          setOpenSubcontractorDrawer(false);
          setSelectedContractorEmail('');
          setSelectedInquiryDTO(null);
          setSelectedPackageName('');
        }}
        contractorEmail={selectedContractorEmail}
        inquiry={selectedInquiryDTO}
        packageName={selectedPackageName}
      />

      <InquiryNotesDrawer
        open={openNotesDrawer}
        onClose={() => {
          setOpenNotesDrawer(false);
          setSelectedInquiryForNotes(null);
        }}
        inquiry={selectedInquiryForNotes}
        offerId={offerId}
        packageName={selectedInquiryForNotes ? packages.find(p => p.package_id === selectedInquiryForNotes.package_id)?.name : undefined}
        onUpdated={() => {
          loadData({ silent: true });
          onRefreshData && onRefreshData();
        }}
      />

      <input
        ref={dateInputRef}
        type="date"
        style={{ position: 'fixed', left: -9999, top: 0, opacity: 0, width: 200, height: 32, pointerEvents: 'none', zIndex: 9999 }}
        onChange={(e) => {
          const val = e.target.value || null;
          const pending = pendingDateUpdate.current;
          if (pending) {
            handleUpdateInquiryField(pending.inquiryId, pending.field, val);
            const uiKey = pending.field === 'last_contact_date' ? 'lastContactDate' : 'plannedContactDate';
            setInquiriesUI(prev => prev.map(i => i.id === pending.inquiryId ? { ...i, [uiKey]: val || '' } : i));
            pendingDateUpdate.current = null;
          }
          const inp = dateInputRef.current;
          if (inp) {
            inp.style.left = '-9999px';
            inp.style.pointerEvents = 'none';
          }
        }}
        onBlur={() => {
          const inp = dateInputRef.current;
          if (inp) {
            inp.style.left = '-9999px';
            inp.style.pointerEvents = 'none';
          }
        }}
      />
    </Box>
  );
};

export default InquiriesTab;
